/**
 * Task validation utilities that match the backend schema constraints
 * Backend schema reference: infrastructure/lib/lambdas/schemas/taskSchema.ts
 */

export const TASK_VALIDATION_LIMITS = {
  NAME_MIN_LENGTH: 1,
  NAME_MAX_LENGTH: 200,
  NOTE_TEXT_MAX_LENGTH: 1000,
  NOTES_MAX_COUNT: 100,
  EMOJI_MAX_LENGTH: 10,
  LABEL_MAX_LENGTH: 50,
  LABELS_MAX_COUNT: 50,
};

/**
 * Validates task name
 * @param {string} name - The task name to validate
 * @returns {{ isValid: boolean, error: string }} Validation result
 */
export const validateTaskName = (name) => {
  if (!name || name.trim().length === 0) {
    return { isValid: false, error: "Task name is required" };
  }
  if (name.length > TASK_VALIDATION_LIMITS.NAME_MAX_LENGTH) {
    return {
      isValid: false,
      error: `Task name must be ${TASK_VALIDATION_LIMITS.NAME_MAX_LENGTH} characters or less`,
    };
  }
  return { isValid: true, error: "" };
};

/**
 * Validates a single note text
 * @param {string} noteText - The note text to validate
 * @returns {{ isValid: boolean, error: string }} Validation result
 */
export const validateNoteText = (noteText) => {
  if (
    noteText &&
    noteText.length > TASK_VALIDATION_LIMITS.NOTE_TEXT_MAX_LENGTH
  ) {
    return {
      isValid: false,
      error: `Note must be ${TASK_VALIDATION_LIMITS.NOTE_TEXT_MAX_LENGTH} characters or less`,
    };
  }
  return { isValid: true, error: "" };
};

/**
 * Validates notes count
 * @param {Object} notes - The notes object
 * @param {boolean} hasUnsavedNote - Whether there's an unsaved note in input
 * @returns {{ isValid: boolean, error: string }} Validation result
 */
export const validateNotesCount = (notes, hasUnsavedNote = false) => {
  const noteCount = notes ? Object.keys(notes).length : 0;
  const totalCount = noteCount + (hasUnsavedNote ? 1 : 0);

  if (totalCount > TASK_VALIDATION_LIMITS.NOTES_MAX_COUNT) {
    return {
      isValid: false,
      error: `Maximum ${TASK_VALIDATION_LIMITS.NOTES_MAX_COUNT} notes allowed`,
    };
  }
  return { isValid: true, error: "" };
};

/**
 * Validates all notes in a task
 * @param {Object} notes - The notes object
 * @param {string} unsavedNoteText - Text of unsaved note in input
 * @returns {{ isValid: boolean, errors: Array<{ noteId: string, error: string }>, countError: string }} Validation result
 */
export const validateAllNotes = (notes, unsavedNoteText = "") => {
  const result = {
    isValid: true,
    errors: [],
    countError: "",
    unsavedNoteError: "",
  };

  // Validate count
  const hasUnsavedNote = unsavedNoteText && unsavedNoteText.trim().length > 0;
  const countValidation = validateNotesCount(notes, hasUnsavedNote);
  if (!countValidation.isValid) {
    result.isValid = false;
    result.countError = countValidation.error;
  }

  // Validate each note's text
  if (notes) {
    Object.entries(notes).forEach(([noteId, note]) => {
      const textValidation = validateNoteText(note.text);
      if (!textValidation.isValid) {
        result.isValid = false;
        result.errors.push({ noteId, error: textValidation.error });
      }
    });
  }

  // Validate unsaved note text
  if (unsavedNoteText) {
    const unsavedValidation = validateNoteText(unsavedNoteText);
    if (!unsavedValidation.isValid) {
      result.isValid = false;
      result.unsavedNoteError = unsavedValidation.error;
    }
  }

  return result;
};

/**
 * Validates emoji
 * @param {string} emoji - The emoji to validate
 * @returns {{ isValid: boolean, error: string }} Validation result
 */
export const validateEmoji = (emoji) => {
  if (emoji && emoji.length > TASK_VALIDATION_LIMITS.EMOJI_MAX_LENGTH) {
    return {
      isValid: false,
      error: `Emoji must be ${TASK_VALIDATION_LIMITS.EMOJI_MAX_LENGTH} characters or less`,
    };
  }
  return { isValid: true, error: "" };
};

/**
 * Validates a complete task object before submission
 * @param {Object} task - The task to validate
 * @param {string} unsavedNoteText - Text of unsaved note in input
 * @returns {{ isValid: boolean, errors: Object }} Validation result with field-specific errors
 */
export const validateTask = (task, unsavedNoteText = "") => {
  const errors = {};
  let isValid = true;

  // Validate name
  const nameValidation = validateTaskName(task.name);
  if (!nameValidation.isValid) {
    errors.name = nameValidation.error;
    isValid = false;
  }

  // Validate emoji
  if (task.emoji) {
    const emojiValidation = validateEmoji(task.emoji);
    if (!emojiValidation.isValid) {
      errors.emoji = emojiValidation.error;
      isValid = false;
    }
  }

  // Validate notes
  const notesValidation = validateAllNotes(task.notes, unsavedNoteText);
  if (!notesValidation.isValid) {
    if (notesValidation.countError) {
      errors.notesCount = notesValidation.countError;
    }
    if (notesValidation.errors.length > 0) {
      errors.notes = notesValidation.errors;
    }
    if (notesValidation.unsavedNoteError) {
      errors.unsavedNote = notesValidation.unsavedNoteError;
    }
    isValid = false;
  }

  return { isValid, errors };
};

/**
 * Parses backend validation errors into user-friendly messages
 * @param {Array} backendErrors - Array of AJV validation errors from backend
 * @returns {Object} Object with field-specific error messages
 */
export const parseBackendValidationErrors = (backendErrors) => {
  const errors = {};

  if (!backendErrors || !Array.isArray(backendErrors)) {
    return { general: "An error occurred while saving the task" };
  }

  backendErrors.forEach((error) => {
    const path = error.instancePath || "";
    const keyword = error.keyword;

    if (path === "/name" || path.includes("/name")) {
      if (keyword === "minLength") {
        errors.name = "Task name is required";
      } else if (keyword === "maxLength") {
        errors.name = `Task name must be ${TASK_VALIDATION_LIMITS.NAME_MAX_LENGTH} characters or less`;
      }
    } else if (path.includes("/notes")) {
      if (keyword === "maxProperties") {
        errors.notesCount = `Maximum ${TASK_VALIDATION_LIMITS.NOTES_MAX_COUNT} notes allowed`;
      } else if (path.includes("/text") && keyword === "maxLength") {
        errors.notes = errors.notes || [];
        // Extract note ID from path like /notes/123456/text
        const noteIdMatch = path.match(/\/notes\/(\d+)/);
        if (noteIdMatch) {
          errors.notes.push({
            noteId: noteIdMatch[1],
            error: `Note must be ${TASK_VALIDATION_LIMITS.NOTE_TEXT_MAX_LENGTH} characters or less`,
          });
        }
      }
    } else if (path === "/emoji" && keyword === "maxLength") {
      errors.emoji = `Emoji must be ${TASK_VALIDATION_LIMITS.EMOJI_MAX_LENGTH} characters or less`;
    } else if (!errors.general) {
      errors.general = error.message || "Invalid task format";
    }
  });

  return errors;
};
